import { entitySelectionPayload } from '../../../../shared/utils/entitySelection';
import { hasSelectedEntity } from '../../../../shared/utils/entitySelection';
import { isOverdueItem } from '../../../../shared/utils/overdue';
import { getPaymentAccountingDate } from '../../utils/accountingDates';
import { useEffect, useMemo, useRef, useState } from 'react';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import {
  listInvoicesResult,
  listPaymentsWithClientIdsResult,
  registerPayment as registerPaymentMutation,
  reversePayment as reversePaymentMutation,
  restorePayment as restorePaymentMutation,
  cancelPayment as cancelPaymentMutation,
  updatePayment as updatePaymentMutation,
  updateInvoice as updateInvoiceMutation,
  voidInvoice as voidInvoiceMutation,
} from '@/modules/accounting/services';
import { useAccountingFilters } from '@/modules/accounting/hooks/shared/useAccountingFilters';
import {
  isWithinNextDays,
  sortByDueDateAscending,
} from '@/modules/accounting/utils';
import { toast } from '../../../../shared/hooks/use-toast';

const ACCOUNTING_INVOICE_CREATED_EVENT = 'accounting:invoice-created';

const INITIAL_FILTERS = {
  query: '',
  selectedClient: 'tutti',
  selectedPeriod: 'tutti',
  selectedStatus: 'tutti',
  selectedPaymentType: 'tutti',
};

const NON_OPERATIONAL_PAYMENT_STATUSES = new Set(['stornato', 'annullato']);

function isOperationalPayment(payment) {
  return !NON_OPERATIONAL_PAYMENT_STATUSES.has(payment.status);
}

function normalizeClientId(clientId) {
  if (clientId === null || clientId === undefined || clientId === '') {
    return '';
  }

  return String(clientId);
}

function getClientDisplayName(client) {
  return client?.company || client?.businessName || client?.name || '';
}

function buildClientsMap(clients) {
  return new Map(
    (clients || [])
      .map((client) => [normalizeClientId(client?.id), client])
      .filter(([clientId]) => clientId)
  );
}

function resolveClientReference({ clientId, legacyClientName, clientsMap }) {
  const normalizedClientId = normalizeClientId(clientId);
  const legacyName = String(legacyClientName || '').trim();

  if (!normalizedClientId) {
    return {
      clientResolvedName: legacyName || 'Cliente non risolto',
      clientResolutionStatus: legacyName ? 'legacy_fallback' : 'missing_client_id',
      clientLegacyName: legacyName,
    };
  }

  const client = clientsMap.get(normalizedClientId);
  if (client) {
    return {
      clientResolvedName:
        getClientDisplayName(client) || legacyName || `Cliente ${normalizedClientId}`,
      clientResolutionStatus: 'resolved',
      clientLegacyName: legacyName,
    };
  }

  return {
    clientResolvedName: legacyName || 'Cliente non risolto',
    clientResolutionStatus: 'unresolved_client_reference',
    clientLegacyName: legacyName,
  };
}

function enrichAccountingClientReferences(items, clientsMap) {
  return (items || []).map((item) => {
    const resolution = resolveClientReference({
      clientId: item.clientId,
      legacyClientName: item.client,
      clientsMap,
    });

    return {
      ...item,
      ...resolution,
      client: resolution.clientResolvedName,
    };
  });
}

function normalizeSearch(value) {
  return String(value || '').trim().toLowerCase();
}

function matchesSearchQuery(item, query, fields) {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return true;

  return fields.some((field) => normalizeSearch(item?.[field]).includes(normalizedQuery));
}

function buildPaymentsByInvoiceId(payments) {
  return payments.reduce((acc, payment) => {
    if (payment.invoiceId === null || payment.invoiceId === undefined) return acc;
    const key = String(payment.invoiceId);
    acc.set(key, [...(acc.get(key) || []), payment]);
    return acc;
  }, new Map());
}

function getInvoiceLinkedPayments(invoice, paymentsByInvoiceId) {
  if (invoice?.id === null || invoice?.id === undefined) return [];
  return paymentsByInvoiceId.get(String(invoice.id)) || [];
}

function invoiceMatchesFilters(invoice, linkedPayments, filters) {
  const matchesInvoiceQuery = matchesSearchQuery(invoice, filters.query, [
    'invoiceNumber',
    'client',
    'clientResolvedName',
    'assignment',
    'status',
    'totalAmount',
    'issueDate',
    'notes',
  ]);
  const matchesLinkedPaymentQuery = linkedPayments.some((payment) =>
    matchesSearchQuery(payment, filters.query, [
      'invoiceNumber',
      'client',
      'clientResolvedName',
      'assignment',
      'type',
      'status',
      'notes',
      'amount',
      'issueDate',
      'dueDate',
      'paymentDate',
    ])
  );
  const matchesQuery = !normalizeSearch(filters.query) || matchesInvoiceQuery || matchesLinkedPaymentQuery;
  const matchesClient =
    filters.selectedClient === 'tutti' ||
    hasSelectedEntity(invoice, 'client', filters.selectedClient);
  const matchesStatus =
    filters.selectedStatus === 'tutti' ||
    invoice.status === filters.selectedStatus ||
    linkedPayments.some((payment) => payment.status === filters.selectedStatus);
  const matchesPaymentType =
    filters.selectedPaymentType === 'tutti' ||
    linkedPayments.some((payment) => payment.type === filters.selectedPaymentType);

  if (filters.selectedPeriod === 'scaduti') {
    return matchesQuery && matchesClient && matchesPaymentType && linkedPayments.some((payment) => payment.status === 'in_ritardo');
  }

  if (filters.selectedPeriod === 'prossimi_30') {
    return matchesQuery && matchesClient && matchesStatus && matchesPaymentType && linkedPayments.some((payment) => isWithinNextDays(payment.dueDate, 30));
  }

  return matchesQuery && matchesClient && matchesStatus && matchesPaymentType;
}

export function useAccountingIncomeData({ initialClientId = '', periodFrom = '', periodTo = '' } = {}) {
  const [clientsState, setClientsState] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoadingIncome, setIsLoadingIncome] = useState(true);
  const [incomeModuleDisabled, setIncomeModuleDisabled] = useState(false);
  const [incomeAuthRequired, setIncomeAuthRequired] = useState(false);
  const [incomeTenantDatabaseError, setIncomeTenantDatabaseError] = useState('');
  const [paymentsUnavailable, setPaymentsUnavailable] = useState(false);
  const [invoicesUnavailable, setInvoicesUnavailable] = useState(false);
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [loadError, setLoadError] = useState('');
  const { filters, setFilter } = useAccountingFilters(INITIAL_FILTERS);
  const [activeOverviewFilter, setActiveOverviewFilter] = useState(null);
  const normalizedInitialClientId = normalizeClientId(initialClientId);
  const appliedInitialClientId = useRef('');

  useEffect(() => {
    if (!normalizedInitialClientId || appliedInitialClientId.current === normalizedInitialClientId) return;
    appliedInitialClientId.current = normalizedInitialClientId;
    setFilter('selectedClient', normalizedInitialClientId);
  }, [normalizedInitialClientId, setFilter]);

  useEffect(() => {
    let active = true;

    async function loadIncomeReadModels() {
      const [paymentsResult, invoicesResult, clientsResult] = await Promise.all([
        listPaymentsWithClientIdsResult(),
        listInvoicesResult(),
        listClientsResult(),
      ]);
      if (!active) return;

      const moduleDisabled =
        paymentsResult.error === 'ACCOUNTING_MODULE_DISABLED' ||
        invoicesResult.error === 'ACCOUNTING_MODULE_DISABLED';
      const authRequired =
        paymentsResult.error === 'ACCOUNTING_AUTH_REQUIRED' ||
        invoicesResult.error === 'ACCOUNTING_AUTH_REQUIRED' ||
        clientsResult.error === 'CLIENTS_AUTH_REQUIRED';
      const tenantDatabaseError =
        [
          'ACCOUNTING_TENANT_DATABASE_NOT_READY',
          'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
          'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
        ].includes(paymentsResult.error) ||
        [
          'ACCOUNTING_TENANT_DATABASE_NOT_READY',
          'ACCOUNTING_TENANT_DATABASE_UNAVAILABLE',
          'ACCOUNTING_TENANT_DATABASE_METADATA_MISSING',
        ].includes(invoicesResult.error)
          ? paymentsResult.error && paymentsResult.error.startsWith('ACCOUNTING_TENANT_DATABASE_')
            ? paymentsResult.error
            : invoicesResult.error
          : '';

      setIncomeModuleDisabled(moduleDisabled);
      setIncomeAuthRequired(authRequired && !moduleDisabled);
      setIncomeTenantDatabaseError(tenantDatabaseError);
      setPaymentsUnavailable(
        !paymentsResult.ok &&
          paymentsResult.error !== 'ACCOUNTING_AUTH_REQUIRED' &&
          paymentsResult.error !== 'ACCOUNTING_MODULE_DISABLED' &&
          !paymentsResult.error?.startsWith('ACCOUNTING_TENANT_DATABASE_')
      );
      setInvoicesUnavailable(
        !invoicesResult.ok &&
          invoicesResult.error !== 'ACCOUNTING_AUTH_REQUIRED' &&
          invoicesResult.error !== 'ACCOUNTING_MODULE_DISABLED' &&
          !invoicesResult.error?.startsWith('ACCOUNTING_TENANT_DATABASE_')
      );
      setClientsUnavailable(!clientsResult.ok && clientsResult.error !== 'CLIENTS_AUTH_REQUIRED');
      const clients = clientsResult.ok ? clientsResult.data : [];
      const clientsMap = buildClientsMap(clients);

      setPayments(
        enrichAccountingClientReferences(paymentsResult.ok ? paymentsResult.data : [], clientsMap)
      );
      setInvoices(
        enrichAccountingClientReferences(invoicesResult.ok ? invoicesResult.data : [], clientsMap)
      );
      setClientsState(clients);
      setLoadError(
        moduleDisabled
          ? paymentsResult.message ||
            invoicesResult.message ||
            'Modulo accounting non abilitato per questo workspace.'
          : tenantDatabaseError
            ? paymentsResult.message ||
              invoicesResult.message ||
              'Database tenant accounting non disponibile.'
          : authRequired
            ? 'Sessione non autorizzata per il backend accounting.'
          : !paymentsResult.ok || !invoicesResult.ok || !clientsResult.ok
            ? 'Backend accounting non completamente disponibile. Mostro un read-model reale parziale o vuoto.'
          : ''
      );
      setIsLoadingIncome(false);
    }

    loadIncomeReadModels();
    window.addEventListener('webtika:deal-payments-updated', loadIncomeReadModels);

    return () => {
      window.removeEventListener('webtika:deal-payments-updated', loadIncomeReadModels);
      active = false;
    };
  }, []);

  const clientScopedPayments = useMemo(() => {
    return payments.filter((payment) =>
      filters.selectedClient === 'tutti' ||
      hasSelectedEntity(payment, 'client', filters.selectedClient)
    );
  }, [filters.selectedClient, payments]);

  const filteredPaymentsBeforeOverview = useMemo(() => {
    const filtered = payments.filter((payment) => {
      const matchesQuery = matchesSearchQuery(payment, filters.query, [
        'invoiceNumber',
        'client',
        'clientResolvedName',
        'assignment',
        'type',
        'status',
        'notes',
        'amount',
        'dueDate',
        'paymentDate',
      ]);
      const matchesClient =
        filters.selectedClient === 'tutti' ||
        hasSelectedEntity(payment, 'client', filters.selectedClient);
      const matchesStatus =
        filters.selectedStatus === 'tutti' || payment.status === filters.selectedStatus;
      const matchesPaymentType =
        filters.selectedPaymentType === 'tutti' || payment.type === filters.selectedPaymentType;

      if (filters.selectedPeriod === 'scaduti') {
        return matchesQuery && matchesClient && matchesPaymentType && payment.status === 'in_ritardo';
      }

      if (filters.selectedPeriod === 'prossimi_30') {
        return matchesQuery && matchesClient && matchesStatus && matchesPaymentType && isWithinNextDays(payment.dueDate, 30);
      }

      if (filters.selectedPeriod === 'dashboard_period') {
        const issueDate = String(getPaymentAccountingDate(payment));
        const matchesDashboardPeriod = issueDate &&
          (!periodFrom || issueDate >= periodFrom) &&
          (!periodTo || issueDate <= periodTo);
        return matchesQuery && matchesClient && matchesStatus && matchesPaymentType && matchesDashboardPeriod;
      }

      return matchesQuery && matchesClient && matchesStatus && matchesPaymentType;
    });

    return sortByDueDateAscending(filtered);
  }, [payments, filters, periodFrom, periodTo]);

  const filteredPayments = useMemo(() => {
    if (!activeOverviewFilter) return filteredPaymentsBeforeOverview;
    const operational = filteredPaymentsBeforeOverview.filter(isOperationalPayment);
    if (activeOverviewFilter === 'expected') return sortByDueDateAscending(operational);
    if (activeOverviewFilter === 'received') {
      return sortByDueDateAscending(operational.filter((payment) => payment.status === 'incassato'));
    }
    if (activeOverviewFilter === 'toReceive') {
      return sortByDueDateAscending(operational.filter((payment) => payment.status !== 'incassato'));
    }
    if (activeOverviewFilter === 'overdue') {
      return sortByDueDateAscending(operational.filter((payment) => isOverdueItem(payment)));
    }
    return sortByDueDateAscending(operational);
  }, [activeOverviewFilter, filteredPaymentsBeforeOverview]);

  const paymentsByInvoiceId = useMemo(() => buildPaymentsByInvoiceId(payments), [payments]);
  const filteredPaymentInvoiceIds = useMemo(
    () => new Set(
      filteredPayments
        .filter((payment) => payment.invoiceId !== null && payment.invoiceId !== undefined)
        .map((payment) => String(payment.invoiceId))
    ),
    [filteredPayments]
  );

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const linkedPayments = getInvoiceLinkedPayments(invoice, paymentsByInvoiceId);
      if (activeOverviewFilter && !linkedPayments.some((payment) => filteredPaymentInvoiceIds.has(String(payment.invoiceId)))) {
        return false;
      }
      return invoiceMatchesFilters(invoice, linkedPayments, filters);
    });
  }, [activeOverviewFilter, filteredPaymentInvoiceIds, invoices, paymentsByInvoiceId, filters]);

  const stats = useMemo(() => {
    const operationalFilteredPayments = clientScopedPayments.filter(isOperationalPayment);
    const totalExpected = operationalFilteredPayments.reduce((sum, item) => sum + item.amount, 0);
    const totalReceived = operationalFilteredPayments
      .filter((item) => item.status === 'incassato')
      .reduce((sum, item) => sum + item.amount, 0);
    const totalPending = operationalFilteredPayments
      .filter((item) => item.status !== 'incassato')
      .reduce((sum, item) => sum + item.amount, 0);
    const totalOverdue = operationalFilteredPayments
      .filter((item) => isOverdueItem(item))
      .reduce((sum, item) => sum + item.amount, 0);

    return {
      totalExpected,
      totalReceived,
      totalPending,
      totalOverdue,
      overdueCount: operationalFilteredPayments.filter(isOverdueItem).length,
      pendingCount: operationalFilteredPayments.filter((item) => item.status === 'previsto').length,
      totalCount: operationalFilteredPayments.length,
      percentageReceived:
        totalExpected > 0 ? ((totalReceived / totalExpected) * 100).toFixed(1) : '0.0',
    };
  }, [clientScopedPayments]);

  const upcomingPayments = useMemo(() => {
    return sortByDueDateAscending(
      filteredPayments.filter((payment) => payment.status === 'previsto' && isWithinNextDays(payment.dueDate, 7))
    );
  }, [filteredPayments]);

  function createInvoiceResult({ invoice, payments: generatedPayments }) {
    setInvoices((prev) =>
      [...prev, invoice].sort((left, right) => right.issueDate.localeCompare(left.issueDate))
    );
    setPayments((prev) =>
      [...prev, ...generatedPayments].sort((left, right) => left.dueDate.localeCompare(right.dueDate))
    );
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(ACCOUNTING_INVOICE_CREATED_EVENT, {
          detail: {
            completedDealId: invoice?.completedDealId ?? null,
          },
        })
      );
    }
  }
  function immediatePaymentCreated(payment) {
    const clientsMap=buildClientsMap(clientsState);
    setPayments((current)=>sortByDueDateAscending([...current,...enrichAccountingClientReferences([payment],clientsMap)]));
  }

  async function registerPayment(paymentId) {
    const result = await registerPaymentMutation(paymentId);
    if (!result.ok) {
      toast({ title: 'Incasso non registrato', description: result.message, variant: 'destructive' });
      return result;
    }

    setPayments((prev) =>
      prev.map((item) =>
        item.id === paymentId
          ? {
              ...item,
              ...result.data.payment,
              clientId: result.data.payment.clientId ?? item.clientId ?? null,
              client:
                result.data.payment.clientResolvedName ||
                item.clientResolvedName ||
                result.data.payment.client ||
                item.client,
              clientResolvedName:
                result.data.payment.clientResolvedName ||
                item.clientResolvedName ||
                result.data.payment.client ||
                item.client,
              clientResolutionStatus:
                result.data.payment.clientResolutionStatus ||
                item.clientResolutionStatus ||
                'legacy_fallback',
              clientLegacyName:
                result.data.payment.clientLegacyName ||
                item.clientLegacyName ||
                result.data.payment.client ||
                item.client,
            }
          : item
      )
    );
    toast({ title: 'Incasso registrato', description: result.message });
    return result;
  }

  async function restorePayment(paymentId) {
    const result = await restorePaymentMutation(paymentId);
    if (!result.ok) {
      toast({ title: 'Ripristino non riuscito', description: result.message, variant: 'destructive' });
      return result;
    }

    setPayments((prev) =>
      prev.map((item) => (item.id === paymentId ? { ...item, ...result.data.payment } : item))
    );
    toast({ title: 'Incasso ripristinato', description: result.message });
    return result;
  }

  async function reversePayment(paymentId) {
    const result = await reversePaymentMutation(paymentId);
    if (!result.ok) {
      toast({ title: 'Storno non riuscito', description: result.message, variant: 'destructive' });
      return result;
    }

    setPayments((prev) =>
      prev.map((item) => (item.id === paymentId ? { ...item, ...result.data.payment } : item))
    );
    toast({ title: 'Incasso stornato', description: result.message });
    return result;
  }

  async function cancelPayment(paymentId) {
    const result = await cancelPaymentMutation(paymentId);
    if (!result.ok) {
      toast({ title: 'Annullamento non riuscito', description: result.message, variant: 'destructive' });
      return result;
    }
    setPayments((prev) => prev.map((item) => item.id === paymentId ? { ...item, ...result.data.payment } : item));
    toast({ title: 'Incasso annullato', description: result.message });
    return result;
  }

  async function updatePayment(paymentId, payload) {
    const result = await updatePaymentMutation(paymentId, payload);
    if (!result.ok) {
      toast({ title: 'Incasso non aggiornato', description: result.message, variant: 'destructive' });
      return result;
    }

    setPayments((prev) =>
      prev.map((item) =>
        item.id === paymentId
          ? {
              ...item,
              ...result.data.payment,
              clientId: result.data.payment.clientId ?? item.clientId ?? null,
              client: (item.clientId ? item.clientResolvedName : null) || result.data.payment.client || item.client,
              clientResolvedName: (item.clientId ? item.clientResolvedName : null) || result.data.payment.client || item.client,
              clientResolutionStatus: item.clientResolutionStatus || 'legacy_fallback',
              clientLegacyName: result.data.payment.client || item.clientLegacyName || item.client,
            }
          : item
      )
    );
    toast({ title: 'Incasso aggiornato', description: result.message });
    return result;
  }

  async function voidInvoice(invoiceId) {
    const result = await voidInvoiceMutation(invoiceId);
    if (!result.ok) {
      toast({ title: 'Fattura non annullata', description: result.message, variant: 'destructive' });
      return result;
    }

    setInvoices((prev) =>
      prev.map((item) => (item.id === invoiceId ? { ...item, ...result.data.invoice } : item))
    );
    setPayments((prev) =>
      prev.map((item) =>
        item.invoiceId === invoiceId && item.status === 'previsto'
          ? { ...item, status: 'annullato', paymentDate: null }
          : item
      )
    );
    toast({ title: 'Fattura annullata', description: result.message });
    return result;
  }

  async function updateInvoice(invoiceId, payload) {
    const result = await updateInvoiceMutation(invoiceId, payload);
    if (!result.ok) {
      toast({ title: 'Fattura non aggiornata', description: result.message, variant: 'destructive' });
      return result;
    }
    const updated = result.data.invoice;
    setInvoices((prev) => prev.map((item) => item.id === invoiceId ? { ...item, ...updated } : item));
    setPayments((prev) => prev.map((item) => item.invoiceId === invoiceId ? {
      ...item, ...entitySelectionPayload(updated), client: updated.client, clientId: updated.clientId, assignment: updated.assignment,
      clientResolvedName: updated.client,
    } : item));
    window.dispatchEvent(new CustomEvent('accounting:invoice-updated', { detail: { invoiceId } }));
    toast({ title: 'Fattura aggiornata', description: result.message });
    return result;
  }

  function setOverviewFilter(filterKey) {
    setActiveOverviewFilter((prev) => {
      const next = prev === filterKey ? null : filterKey;
      const selectedClient = filters.selectedClient;
      Object.entries({
        query: '',
        selectedClient,
        selectedPeriod: 'tutti',
        selectedStatus: 'tutti',
        selectedPaymentType: 'tutti',
      }).forEach(([key, value]) => setFilter(key, value));
      return next;
    });
  }

  function clearOverviewFilter() {
    setActiveOverviewFilter(null);
  }

  return {
    clients: clientsState,
    invoices,
    payments,
    isLoadingIncome,
    incomeModuleDisabled,
    incomeAuthRequired,
    incomeTenantDatabaseError,
    paymentsUnavailable,
    invoicesUnavailable,
    clientsUnavailable,
    incomeUnavailable:
      !incomeModuleDisabled &&
      !incomeAuthRequired &&
      (paymentsUnavailable || invoicesUnavailable || clientsUnavailable),
    loadError,
    incomeCatalogStatus:
      incomeModuleDisabled
        ? 'module_disabled'
        : incomeAuthRequired
          ? 'auth_required'
          : incomeTenantDatabaseError
            ? 'tenant_database_error'
          : paymentsUnavailable || invoicesUnavailable || clientsUnavailable
        ? 'unavailable'
        : payments.length > 0 || invoices.length > 0 || clientsState.length > 0
          ? 'available'
          : 'empty',
    filteredPayments,
    filteredInvoices,
    upcomingPayments,
    stats,
    filters,
    setFilter,
    activeOverviewFilter,
    setOverviewFilter,
    clearOverviewFilter,
    createInvoiceResult,
    immediatePaymentCreated,
    registerPayment,
    reversePayment,
    restorePayment,
    cancelPayment,
    updatePayment,
    updateInvoice,
    voidInvoice,
  };
}
